import { Card, CardContent } from '../../ui/card';
import { UploadIcon, FileIcon, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useUpload } from '../hooks/useUpload';
import { useState, useCallback, useMemo } from 'react';
import { cn } from '../../../utils/utils';

const uploadTypes = [
  { key: 'sales_order', label: 'FC Sales Order' },
  { key: 'sellin', label: 'FC Sell-in' },
  { key: 'ats', label: 'FC ATS (Test only)' },
] as const;

type UploadType = typeof uploadTypes[number]['key'];

interface UploadCardProps {
  uploadType: UploadType;
  title: string;
}


const UploadCard: React.FC<UploadCardProps> = ({ uploadType, title }) => {
  const [isDragging, setIsDragging] = useState(false);

  const {
    fileInputRef,
    uploading,
    progress,
    uploadError,
    uploadResponse,
    statusMessage,
    handleFileChange,
    handleUpload,
  } = useUpload();

  // Memoize the selected file info
  const selectedFile = useMemo(() => {
    return fileInputRef.current?.files?.[0];
  }, [fileInputRef.current?.files]);

  // Handle drag events with useCallback to prevent unnecessary re-renders
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      fileInputRef.current!.files = e.dataTransfer.files;
      handleFileChange({ target: { files: e.dataTransfer.files } } as any);
    }
  }, [fileInputRef, handleFileChange]);

  const handleFileInputClick = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const handleUploadClick = useCallback(() => {
    handleUpload(uploadType);
  }, [handleUpload, uploadType]);

  // Format file size helper
  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Get upload status styling
  const getStatusStyling = useCallback((type: 'success' | 'error' | 'info') => {
    switch (type) {
      case 'success':
        return 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-900/20 dark:border-emerald-800';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800';
      case 'info':
        return 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800';
      default:
        return '';
    }
  }, []);

  return (
    <div className="p-6">
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">{title}</h2>
            <p className="text-sm text-muted-foreground">
              Upload CSV, Excel, or JSON files
            </p>
          </div>

          {/* Upload Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleFileInputClick}
            className={cn(
              "relative w-full h-40 border-2 border-dashed rounded-lg mb-6 flex flex-col justify-center items-center text-center cursor-pointer transition-all duration-200",
              "hover:border-primary/50 hover:bg-accent/50",
              isDragging 
                ? "border-primary bg-primary/10 scale-[1.02]" 
                : "border-border",
              uploading && "pointer-events-none opacity-50"
            )}
            role="button"
            tabIndex={0}
            aria-label="Upload file area"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleFileInputClick();
              }
            }}
          >
            <div className="flex flex-col items-center space-y-3">
              {selectedFile ? (
                <>
                  <FileIcon className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <UploadIcon className={cn(
                    "h-8 w-8 transition-colors",
                    isDragging ? "text-primary" : "text-muted-foreground"
                  )} />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {isDragging ? "Drop file here" : "Drag & drop file here"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      or click to browse
                    </p>
                  </div>
                </>
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".csv,.xlsx,.xls,.json"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
              aria-label="File input"
            />
          </div>

          {/* Progress Bar */}
          {uploading && (
            <div className="mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Uploading...</span>
                <span className="font-medium">{Math.round(progress)}%</span>
              </div>
              <div className="relative w-full bg-secondary rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-primary to-primary/80 h-full transition-all duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
              {progress === 100 && (
                <p className="text-xs text-muted-foreground text-center">
                  Processing file...
                </p>
              )}
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUploadClick}
            disabled={uploading || !selectedFile}
            className={cn(
              "w-full py-3 px-4 rounded-lg font-medium transition-all duration-200",
              "bg-primary hover:bg-primary/90 text-primary-foreground",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            )}
          >
            {uploading ? (
              <span className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                <span>Uploading...</span>
              </span>
            ) : (
              'Upload File'
            )}
          </button>

          {/* Status Messages */}
          {statusMessage && (
            <div className={cn(
              "mt-4 p-3 rounded-lg border flex items-start space-x-2",
              getStatusStyling('success')
            )}>
              <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium">{statusMessage}</p>
            </div>
          )}

          {uploadError && (
            <div className={cn(
              "mt-4 p-3 rounded-lg border flex items-start space-x-2",
              getStatusStyling('error')
            )}>
              <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium">{uploadError}</p>
            </div>
          )}

          {/* Upload Response Details */}
          {uploadResponse && (
            <div className={cn(
              "mt-4 p-4 rounded-lg border",
              getStatusStyling('info')
            )}>
              <div className="flex items-center space-x-2 mb-3">
                <AlertCircle className="h-4 w-4" />
                <h3 className="text-sm font-semibold">Upload Summary</h3>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">File:</span>
                    <span className="ml-2">{uploadResponse.filename}</span>
                  </div>
                  <div>
                    <span className="font-medium">Size:</span>
                    <span className="ml-2">{uploadResponse.file_size_mb} MB</span>
                  </div>
                  <div>
                    <span className="font-medium">Processing Time:</span>
                    <span className="ml-2">{uploadResponse.processing_time_seconds}s</span>
                  </div>
                  <div>
                    <span className="font-medium">User:</span>
                    <span className="ml-2">{uploadResponse.user}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">Rows Processed:</span>
                    <span className="ml-2 text-green-600">{(uploadResponse.processed_rows ?? 0).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="font-medium">New Records:</span>
                    <span className="ml-2 text-green-600 font-semibold">
                      +{(uploadResponse.new_records ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Updated Records:</span>
                    <span className="ml-2 text-green-600 font-semibold">
                      {(uploadResponse.updated_records ?? 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              
              {uploadResponse.message && (
                <div className="mt-3 pt-3 border-t border-current/20">
                  <p className="text-sm">
                    <span className="font-medium">Message:</span>
                    <span className="ml-2">{uploadResponse.message}</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadCard;